import { formatarAteDuasCasas, formatarDataHora, formatarReais } from '@/lib/formato';

import type { AbastecimentoDaApi, EnvioDeAbastecimento } from './api';
import type { Abastecimento } from './tipos';

/**
 * ESTORNAR E CORRIGIR O ABASTECIMENTO (ADR-008, migração 0087) — as regras que
 * a mesa e o celular dividem.
 *
 * O livro é só de inserção. Estornar acrescenta a anulação (o volume volta ao
 * tanque no interno; o externo sai da cobrança ainda aberta); corrigir é
 * estornar e lançar o certo na mesma transação. O servidor decide o que pode —
 * cobrança conciliada, por exemplo, ele recusa com a frase do que fazer —, e
 * estas funções só decidem o que a tela OFERECE.
 */

/** O motivo do estorno tem piso de 15 caracteres, o mesmo do banco (`org.estorno`). */
export const PISO_DO_MOTIVO_DO_ESTORNO = 15;

export interface AcoesDoAbastecimento {
  readonly estornar: boolean;
  readonly corrigir: boolean;
  /** Por que não há ação, escrito no lugar dela; `null` quando há. */
  readonly porQueNao: string | null;
}

/**
 * O que a linha deixa fazer. `permissoes` nulo é "a sessão não diz" — e aí as
 * ações aparecem, porque a rota confere de qualquer forma (a mesma regra do
 * livro do tanque). Corrigir lança um abastecimento novo: pede também a
 * permissão de lançar.
 */
export function acoesDoAbastecimento(
  a: Pick<AbastecimentoDaApi, 'estornadoEm' | 'corrigidoPorId'>,
  permissoes: readonly string[] | null | undefined,
): AcoesDoAbastecimento {
  if (a.estornadoEm) {
    return {
      estornar: false,
      corrigir: false,
      porQueNao: a.corrigidoPorId
        ? 'Corrigido: o lançamento certo entrou no lugar dele.'
        : 'Estornado: o lançamento fica na lista, anulado, e não conta no consumo nem no custo.',
    };
  }
  const tem = (codigo: string) => permissoes === null || permissoes === undefined || permissoes.includes(codigo);
  const estornar = tem('abastecimento.estornar');
  const corrigir = estornar && tem('abastecimento.registrar');
  return {
    estornar,
    corrigir,
    porQueNao: estornar ? null : 'Corrigir um abastecimento pede a licença de estorno do abastecimento. Peça ao administrador da empresa, que a concede em Cadastros › Governança.',
  };
}

/**
 * O MENU DA LINHA do abastecimento (os três pontos, sempre à vista — p.27).
 *
 * Achado dos robôs no QAS (20/10/2026): o administrador lançou e não achou
 * "Corrigir abastecimento" — a linha não tinha os três pontos, e ninguém dizia
 * por quê. A regra é a da ADR-008: `abastecimento.estornar` é licença própria
 * e nasce sem dono (0087); quem concede é o dono, em Cadastros › Governança.
 * No QAS ela não estava concedida a papel nenhum. A tela oferece o que o perfil
 * consegue concluir:
 *
 *  - com a licença, corrigir e estornar (corrigir pede também lançar);
 *  - sem ela, as duas aparecem desligadas com o motivo escrito, e quem gere a
 *    governança recebe o caminho para conceder — a única ação que ele conclui.
 *
 * `governanca` é o item que leva a Perfis e responsáveis; a página decide o
 * endereço.
 */
export interface ItemDoMenuDoAbastecimento {
  readonly acao: 'correcao' | 'estorno' | 'exclusao' | 'governanca';
  readonly rotulo: string;
  readonly motivoIndisponivel?: string;
}

export function menuDoAbastecimento(permissoes: readonly string[] | null | undefined): readonly ItemDoMenuDoAbastecimento[] {
  const pode = acoesDoAbastecimento({ estornadoEm: null, corrigidoPorId: null }, permissoes);
  const tem = (codigo: string) => permissoes === null || permissoes === undefined || permissoes.includes(codigo);
  const semEstorno = 'Estornar e corrigir pedem a licença de estorno do abastecimento. Peça ao administrador da empresa, que a concede em Cadastros › Governança.';
  const itens: ItemDoMenuDoAbastecimento[] = [
    pode.corrigir
      ? { acao: 'correcao', rotulo: 'Corrigir abastecimento' }
      : {
          acao: 'correcao',
          rotulo: 'Corrigir abastecimento',
          motivoIndisponivel: pode.estornar
            ? 'Corrigir lança o abastecimento certo no lugar: pede também a licença de lançar abastecimento. Peça ao administrador da empresa.'
            : semEstorno,
        },
    pode.estornar
      ? { acao: 'estorno', rotulo: 'Estornar abastecimento' }
      : { acao: 'estorno', rotulo: 'Estornar abastecimento', motivoIndisponivel: semEstorno },
  ];
  /* APAGAR (0126) SÓ APARECE PARA QUEM PODE, e é a única ação do menu que se
     esconde em vez de vir desligada com o motivo.

     As outras vêm desligadas por causa da cicatriz da ADR-008: o administrador
     não achou "Corrigir" e ninguém disse por quê. Aqui é o contrário — a
     licença é do administrador e de mais ninguém (decisão do dono, 26/10/2026),
     e oferecê-la desligada a toda a empresa anunciaria um poder que quem lê a
     linha não deve sequer considerar. Quem não a tem já tem o caminho certo
     para o erro que aconteceu, que é o estorno.

     EXIGE A LISTA EXPLÍCITA, e não o `tem()` das outras: ele responde "sim"
     quando a sessão ainda não disse (e aí a rota confere), o que aqui faria o
     item piscar para todo mundo enquanto a sessão carrega. */
  if (permissoes?.includes('abastecimento.excluir') === true) {
    itens.push({ acao: 'exclusao', rotulo: 'Apagar lançamento' });
  }
  if (!pode.estornar && tem('governanca.gerir')) {
    itens.push({ acao: 'governanca', rotulo: 'Conceder a correção em Governança' });
  }
  return itens;
}

/**
 * O MENU DE UMA LINHA, já recortado para ela — a única função que as duas
 * listas da mesa leem (a principal, `/combustivel`, e a do lançamento,
 * `/combustivel/lancamento`). `null` quando a linha não tem três pontos.
 *
 * A LINHA ESTORNADA AINDA PODE SER APAGADA (0126): estornar e apagar
 * respondem a perguntas diferentes, e quem estornou por engano o lançamento
 * que nunca deveria ter existido fica com DUAS linhas no lugar de zero.
 * Estornar e corrigir, essas sim, não fazem sentido duas vezes — então a
 * linha estornada só tem menu para quem apaga, e só com o apagar.
 */
export function menuDaLinhaDoAbastecimento(
  permissoes: readonly string[] | null | undefined,
  estornado: boolean,
): readonly ItemDoMenuDoAbastecimento[] | null {
  if (estornado && permissoes?.includes('abastecimento.excluir') !== true) return null;
  const itens = menuDoAbastecimento(permissoes);
  return estornado ? itens.filter((item) => item.acao === 'exclusao') : itens;
}

/** O que a correção deixa mudar. O resto vem do lançamento original. */
export interface CamposDaCorrecao {
  readonly litros: number;
  readonly quando: Date;
  /** A leitura do medidor; `null` quando não houve. */
  readonly medidor: number | null;
  /** Só no externo: o preço do cupom. */
  readonly precoLitro: number | null;
  /** Só no externo: o número do cupom ou da nota. */
  readonly comprovante: string | null;
}

/** O lançamento errado, no que a correção precisa dele — venha da lista da mesa ou do celular. */
export interface OriginalDaCorrecao {
  readonly tipo: 'interno' | 'externo';
  readonly equipamentoId: string;
  readonly equipamentoCodigo: string;
  readonly produtoId: string;
  readonly litros: number;
  readonly quando: Date;
  readonly medidor: number | null;
  readonly precoLitro: number | null;
  readonly comprovante: string | null;
  readonly tanqueId: string | null;
  readonly postoId: string | null;
  readonly ordemId: string | null;
  readonly obraId: string | null;
  readonly centroCustoId: string | null;
  readonly operadorId: string | null;
  readonly responsavelId: string | null;
  readonly observacao: string | null;
}

/** Da linha da API (celular). `null` quando a linha não traz o produto — sem ele não há o que relançar. */
export function originalDaApi(a: AbastecimentoDaApi): OriginalDaCorrecao | null {
  if (!a.produtoId) return null;
  return {
    tipo: a.tipo,
    equipamentoId: a.equipamentoId,
    equipamentoCodigo: a.equipamentoCodigo,
    produtoId: a.produtoId,
    litros: a.quantidadeL,
    quando: new Date(a.ocorridoEm),
    medidor: a.medidor,
    precoLitro: a.precoLitro,
    comprovante: a.comprovante,
    tanqueId: a.tanqueId ?? null,
    postoId: a.postoId ?? null,
    ordemId: a.ordemId ?? null,
    obraId: a.obraId ?? null,
    centroCustoId: a.centroCustoId ?? null,
    operadorId: a.operadorId ?? null,
    responsavelId: a.responsavelId ?? null,
    observacao: a.observacao,
  };
}

/** Do domínio do módulo (mesa). `null` quando faltam os ids que só a linha do banco traz. */
export function originalDoDominio(a: Abastecimento): OriginalDaCorrecao | null {
  if (!a.equipamentoId || !a.produtoId || !a.tipo || !a.ids) return null;
  return {
    tipo: a.tipo,
    equipamentoId: a.equipamentoId,
    equipamentoCodigo: a.equipamentoCodigo,
    produtoId: a.produtoId,
    litros: a.litros,
    quando: a.quando,
    medidor: a.horimetro,
    precoLitro: a.precoLitro,
    comprovante: a.comprovante ?? null,
    ...a.ids,
  };
}

/**
 * O lançamento certo, montado sobre o errado: a mesma máquina, o mesmo tanque
 * ou posto, o mesmo produto, a mesma obra, o mesmo centro de custo e as mesmas
 * pessoas — com os litros, a data, o medidor e o cupom da correção.
 *
 * A ORDEM vai junto: o estorno a reabre quando o saldo volta, e o lançamento
 * certo a atende de novo. O interno nunca leva preço (vem do tanque).
 */
export function envioDaCorrecao(original: OriginalDaCorrecao, campos: CamposDaCorrecao): EnvioDeAbastecimento {
  const opcional = <K extends string, V>(chave: K, valor: V | null | undefined): Partial<Record<K, V>> =>
    valor === null || valor === undefined ? {} : ({ [chave]: valor } as Partial<Record<K, V>>);
  const interno = original.tipo === 'interno';
  return {
    tipo: original.tipo,
    equipamentoId: original.equipamentoId,
    produtoId: original.produtoId,
    ocorridoEm: campos.quando.toISOString(),
    quantidadeL: campos.litros,
    ...opcional('tanqueId', interno ? original.tanqueId : null),
    ...opcional('ordemId', interno ? original.ordemId : null),
    ...opcional('postoId', interno ? null : original.postoId),
    ...opcional('precoLitro', interno ? null : campos.precoLitro),
    ...opcional('comprovante', interno ? null : campos.comprovante),
    ...opcional('medidor', campos.medidor),
    ...opcional('operadorId', original.operadorId),
    ...opcional('responsavelId', original.responsavelId),
    ...opcional('obraId', original.obraId),
    ...opcional('centroCustoId', original.centroCustoId),
    ...opcional('observacao', original.observacao),
  };
}

/** A correção precisa mudar alguma coisa: o mesmo lançamento de novo não corrige nada. */
export function correcaoMudaAlgo(original: OriginalDaCorrecao, campos: CamposDaCorrecao): boolean {
  return (
    campos.litros !== original.litros ||
    campos.quando.getTime() !== original.quando.getTime() ||
    campos.medidor !== original.medidor ||
    (original.tipo === 'externo' &&
      (campos.precoLitro !== original.precoLitro || (campos.comprovante ?? '') !== (original.comprovante ?? '')))
  );
}

/**
 * Os estornados ficam RECOLHIDOS (ADR-008, "a lista precisa mostrar estorno
 * recolhido por padrão, e o total nunca pode somar as três linhas"): toda soma
 * do módulo — volume, gasto, consumo, pendência — lê só os que valem.
 */
export function separarEstornados<T extends { readonly estornadoEm?: string | null }>(
  linhas: readonly T[],
): { readonly vigentes: readonly T[]; readonly estornados: readonly T[] } {
  const vigentes: T[] = [];
  const estornados: T[] = [];
  for (const l of linhas) (l.estornadoEm ? estornados : vigentes).push(l);
  return { vigentes, estornados };
}

/* ======================================================================
 * A lista da R06 (p.69): estornado oculto atrás da chave, corrigido com a
 * mudança escrita.
 * ==================================================================== */

/**
 * O que mudou entre o abastecimento estornado e o que o corrigiu, na fala do
 * campo: "litros 320 L → 302 L", "horímetro 4.832 → 4.836".
 */
export function mudancasDoAbastecimento(antes: Abastecimento, depois: Abastecimento): string[] {
  const n = (v: number | null) => (v === null ? '—' : formatarAteDuasCasas(v));
  const mudou: string[] = [];
  if (antes.litros !== depois.litros) mudou.push(`litros ${n(antes.litros)} L → ${n(depois.litros)} L`);
  if ((antes.horimetro ?? null) !== (depois.horimetro ?? null)) {
    const nome = depois.tipoMedidor === 'odometro' ? 'odômetro' : 'horímetro';
    mudou.push(`${nome} ${n(antes.horimetro)} → ${n(depois.horimetro)}`);
  }
  if (antes.quando.getTime() !== depois.quando.getTime()) mudou.push(`data ${formatarDataHora(antes.quando)} → ${formatarDataHora(depois.quando)}`);
  if ((antes.precoLitro ?? null) !== (depois.precoLitro ?? null)) {
    mudou.push(`preço ${antes.precoLitro === null ? '—' : formatarReais(antes.precoLitro)} → ${depois.precoLitro === null ? '—' : formatarReais(depois.precoLitro)}`);
  }
  return mudou;
}

/** A segunda linha do corrigido: "Correção: litros 320 L → 302 L". Sem o original à mão, nula. */
export function textoDaCorrecaoDoAbastecimento(depois: Abastecimento, estornadosPorId: ReadonlyMap<string, Abastecimento>): string | null {
  if (!depois.corrigeId) return null;
  const antes = estornadosPorId.get(depois.corrigeId);
  if (!antes) return 'Correção de um lançamento estornado';
  const mudou = mudancasDoAbastecimento(antes, depois);
  return mudou.length > 0 ? `Correção: ${mudou.join(' · ')}` : 'Correção de um lançamento estornado';
}

/**
 * Junta os estornados aos vigentes NA POSIÇÃO ORIGINAL (a lista é do mais
 * recente para o mais antigo). `sort` é estável: no empate, o vigente vem
 * antes.
 */
export function juntarNaPosicaoOriginal<T>(vigentes: readonly T[], estornados: readonly T[], quando: (linha: T) => Date): T[] {
  return [...vigentes, ...estornados].sort((a, b) => quando(b).getTime() - quando(a).getTime());
}
